import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, isToday, isPast } from 'date-fns';
import { de } from 'date-fns/locale';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { useFamilyContext } from '../context/FamilyContext';
import TodoModal from '../components/TodoModal';

const { FiPlus, FiCheck, FiClock, FiCheckSquare, FiHome, FiUser } = FiIcons;

const HOUSEHOLD_EXAMPLES = [
  { title: 'Staubsaugen', category: 'Haushalt', priority: 'medium' },
  { title: 'Wäsche waschen', category: 'Haushalt', priority: 'high' },
  { title: 'Spülmaschine ausräumen', category: 'Haushalt', priority: 'medium' },
  { title: 'Müll rausbringen', category: 'Haushalt', priority: 'high' }
];

export default function Todos() {
  const { state, dispatch } = useFamilyContext();
  const { todos, familyMembers } = state;
  const [showModal, setShowModal] = useState(false);
  const [activeTab, setActiveTab] = useState('all'); // all, household

  const filteredTodos = todos.filter(t => activeTab === 'all' || t.category === 'Haushalt');

  const addExample = (ex) => {
    dispatch({ type: 'ADD_TODO', payload: { ...ex, completed: false } });
  };

  return (
    <div className="space-y-6 pb-20">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold dark:text-white">Aufgaben</h1>
        <button onClick={() => setShowModal(true)} className="bg-blue-600 text-white px-5 py-2 rounded-xl font-bold flex items-center shadow-lg"><SafeIcon icon={FiPlus} className="mr-2" /> Neu</button>
      </div>

      <div className="flex space-x-2 bg-gray-100 dark:bg-gray-800 p-1 rounded-xl w-fit">
        <button onClick={() => setActiveTab('all')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'all' ? 'bg-white shadow text-blue-600 dark:bg-gray-700 dark:text-white' : 'text-gray-500'}`}>Alle Aufgaben</button>
        <button onClick={() => setActiveTab('household')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${activeTab === 'household' ? 'bg-white shadow text-blue-600 dark:bg-gray-700 dark:text-white' : 'text-gray-500'}`}><SafeIcon icon={FiHome} className="inline mr-1" /> Haushalt</button>
      </div>

      {activeTab === 'household' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {HOUSEHOLD_EXAMPLES.map(ex => (
            <button key={ex.title} onClick={() => addExample(ex)} className="p-3 bg-white border dark:bg-gray-800 dark:border-gray-700 rounded-xl text-left hover:border-blue-500 transition-all group">
              <p className="text-xs font-bold text-blue-500 mb-1">Vorschlag</p>
              <p className="text-sm font-bold dark:text-white line-clamp-1 group-hover:text-blue-600">{ex.title}</p>
            </button>
          ))}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border dark:border-gray-700 divide-y dark:divide-gray-700">
        {filteredTodos.map(todo => (
          <div key={todo.id} className="p-4 flex items-center justify-between group">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => dispatch({ type: 'UPDATE_TODO', payload: { ...todo, completed: !todo.completed } })}
                className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all ${todo.completed ? 'bg-green-500 border-green-500 text-white' : 'border-gray-200 dark:border-gray-600'}`}
              >
                {todo.completed && <SafeIcon icon={FiCheck} className="w-4 h-4" />}
              </button>
              <div>
                <p className={`font-bold dark:text-white ${todo.completed ? 'line-through text-gray-400' : ''}`}>{todo.title}</p>
                <div className="flex items-center space-x-3 mt-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold uppercase ${todo.category === 'Haushalt' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>{todo.category}</span>
                  {todo.dueDate && <span className="text-[10px] text-gray-400 flex items-center"><SafeIcon icon={FiClock} className="mr-1" /> {format(new Date(todo.dueDate), 'dd.MM.')}</span>}
                </div>
              </div>
            </div>
            <button onClick={() => dispatch({ type: 'DELETE_TODO', payload: todo.id })} className="p-2 text-gray-300 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"><SafeIcon icon={FiIcons.FiTrash2} className="w-5 h-5" /></button>
          </div>
        ))}
        {filteredTodos.length === 0 && (
          <div className="p-12 text-center text-gray-400"><SafeIcon icon={FiCheckSquare} className="w-12 h-12 mx-auto mb-3 opacity-20" /><p>Keine Aufgaben gefunden.</p></div>
        )}
      </div>

      {showModal && (
        <TodoModal onClose={() => setShowModal(false)} onSave={(data) => { dispatch({ type: 'ADD_TODO', payload: data }); setShowModal(false); }} />
      )}
    </div>
  );
}